'use client'

import type Stripe from 'stripe'

import React, { useState } from 'react'
import { toast } from '@/lib/toast'

// import CustomDonationInput from "@/new-chatbot-components/CustomDonationInput";
// import StripeTestCards from "@/new-chatbot-components/StripeTestCards";

import { formatAmountForDisplay } from '@/utils/stripe-helpers'
import * as config from '@/config'
import { createOrganizationBillingPortal, createCheckoutSession } from '@/app/actions/stripe'
import getStripe from '@/utils/get-stripejs'
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider
} from '@stripe/react-stripe-js'
import { GeniiusUser } from '@/lib/types'
import { useLogger } from '@/lib/hooks/useLogger';

interface CheckoutFormProps {
  uiMode: Stripe.Checkout.SessionCreateParams.UiMode
  productId: string
  user: GeniiusUser
  is_active: boolean
  is_free_plan: boolean
  team_id?: string
  is_admin?: boolean
  quantity?: number
}

export default function CheckoutForm(props: CheckoutFormProps): React.ReactElement {
  const [loading, setLoading] = useState<boolean>(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const logger = useLogger();

  const formAction = async (): Promise<void> => {
    setLoading(true)
    try {
      if (!props.is_admin) {
        toast.error('Please contact your organization administrator to manage subscription')
        return
      }

      if (props.is_active) {
        const { url: billingPortal } = await createOrganizationBillingPortal(props.team_id || '')

        if (!billingPortal) {
          toast.error('Unable to access billing portal')
          return
        }

        window.location.href = billingPortal
      } else {
        const { url } = await createCheckoutSession({
          uiMode: props.uiMode,
          productId: props.productId,
          user: props.user,
          metadata: {
            team_id: props.team_id || '',
            is_admin: props.is_admin?.toString()
          },
          quantity: Math.max(props.quantity || 5, 5) // Ensure minimum of 5 seats
        })

        window.location.href = url as string
      }
    } catch (error) {
      logger.error(error)
      toast.error('Failed to process subscription request')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button
        className={`w-full py-4 rounded-md text-base transition-all duration-200
          ${
            props.is_free_plan || loading
              ? 'bg-gray-100 border border-gray-200 cursor-default text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400'
              : !props.is_admin
                ? 'bg-gray-100 border border-gray-200 cursor-default text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400'
                : props.is_active
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : 'bg-gray-900 hover:bg-gray-800 text-white dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 transform hover:-translate-y-0.5'
          }`}
        type="submit"
        disabled={
          loading ||
          (props.is_active ? false : props.is_free_plan) ||
          !props.is_admin
        }
        onClick={formAction}
      >
        {loading
          ? 'Loading...'
          : !props.is_admin
            ? 'Contact Admin'
            : props.is_active
              ? 'Manage Subscription'
              : props.is_free_plan
                ? 'Current Plan'
                : 'Subscribe'}
      </button>
      {clientSecret ? (
        <EmbeddedCheckoutProvider
          stripe={getStripe()}
          options={{ clientSecret }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      ) : null}
    </>
  )
}
